import {Component, OnDestroy, OnInit} from '@angular/core';
import {Apollo, gql} from 'apollo-angular';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {catchError, EMPTY, mergeMap, Subject, Subscription} from 'rxjs';
import {ErrorService} from '../../services/error.service';
import {
  ArchiveTemplate_archiveTemplateMutation,
  ArchiveTemplate_archiveTemplateMutationVariables,
} from './__generated__/ArchiveTemplate_archiveTemplateMutation';

const ARCHIVE_TEMPLATE_MUTATION = gql`
  mutation ArchiveTemplate_archiveTemplateMutation($input: BuilderTemplateInput) {
    updateOrCreateBuilderTemplate(input: $input) {
      error
      templateItem {
        id
        archived
      }
    }
  }
`;

@Component({
  selector: 'app-archive-template',
  templateUrl: './archive-template.component.html',
  styleUrls: ['./archive-template.component.scss'],
  standalone: false,
})
export class ArchiveTemplateComponent implements OnInit, OnDestroy {
  templateItemId: string | null = null;
  isArchived: boolean | null = null;
  private archiveTemplate$ = new Subject<boolean>();
  private subscriptions: Subscription[] = [];

  constructor(
    private apollo: Apollo,
    private bsModalRef: BsModalRef,
    private toastr: ToastrService,
    private errorService: ErrorService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.archiveTemplate$
        .pipe(
          mergeMap(archived =>
            this.apollo
              .mutate<
                ArchiveTemplate_archiveTemplateMutation,
                ArchiveTemplate_archiveTemplateMutationVariables
              >({
                mutation: ARCHIVE_TEMPLATE_MUTATION,
                variables: {
                  input: {
                    id: this.templateItemId,
                    archived,
                  },
                },
                update: cache => cache.evict({id: 'ROOT_QUERY', fieldName: 'templates'}),
              })
              .pipe(
                catchError(error => {
                  this.errorService.handleDefault(error);
                  return EMPTY;
                }),
              ),
          ),
        )
        .subscribe(result => {
          const error = result.data?.updateOrCreateBuilderTemplate.error;
          const archivedResult = result.data?.updateOrCreateBuilderTemplate.templateItem?.archived;
          if (error) {
            this.toastr.error($localize`An error has occurred: ${error}.`);
          } else if (archivedResult === true) {
            this.toastr.success($localize`Template archived.`);
          } else if (archivedResult === false) {
            this.toastr.success($localize`Template unarchived.`);
          }
          this.bsModalRef.hide();
        }),
    );
  }

  archiveTemplate(): void {
    if (this.isArchived === null) {
      return;
    }
    this.archiveTemplate$.next(!this.isArchived);
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe);
  }
}
